/** Procedural WebAudio — chiptune BGM + arcade SFX (no asset files). */

let ctx: AudioContext | null = null;
let muted = false;
let master: GainNode | null = null;
let sfxBus: GainNode | null = null;
let musicBus: GainNode | null = null;

type MusicMode = "off" | "menu" | "play";
let musicMode: MusicMode = "off";
let musicTimer: number | null = null;
let musicStep = 0;
let musicDrive = 0; // 0..1 from speed gear
let musicStartedAt = 0;

const MUTE_KEY = "lanedash.sfxMuted";

/** A4 = 440; midi 69 */
function midi(n: number) {
  return 440 * Math.pow(2, (n - 69) / 12);
}

export function loadMutePreference() {
  try {
    muted = localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    muted = false;
  }
  return muted;
}

export function isSfxMuted() {
  return muted;
}

export function setSfxMuted(next: boolean) {
  muted = next;
  try {
    localStorage.setItem(MUTE_KEY, next ? "1" : "0");
  } catch {
    /* ignore */
  }
  if (muted) stopMusic();
  else if (musicMode !== "off") {
    const mode = musicMode;
    musicMode = "off";
    startMusic(mode === "play" ? "play" : "menu");
  }
}

function ac(): AudioContext | null {
  if (typeof window === "undefined" || muted) return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.85;
    master.connect(ctx.destination);
    sfxBus = ctx.createGain();
    sfxBus.gain.value = 0.9;
    sfxBus.connect(master);
    musicBus = ctx.createGain();
    musicBus.gain.value = 0.22;
    musicBus.connect(master);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function ensureBuses(c: AudioContext) {
  if (!master) {
    master = c.createGain();
    master.gain.value = 0.85;
    master.connect(c.destination);
  }
  if (!sfxBus) {
    sfxBus = c.createGain();
    sfxBus.gain.value = 0.9;
    sfxBus.connect(master);
  }
  if (!musicBus) {
    musicBus = c.createGain();
    musicBus.gain.value = 0.22;
    musicBus.connect(master);
  }
}

function tone(
  freq: number,
  dur: number,
  type: OscillatorType,
  gain = 0.07,
  slideTo?: number,
  dest?: AudioNode,
) {
  const c = ac();
  if (!c) return;
  ensureBuses(c);
  const t0 = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(Math.max(20, freq), t0);
  if (slideTo != null) {
    o.frequency.exponentialRampToValueAtTime(
      Math.max(20, slideTo),
      t0 + dur,
    );
  }
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g);
  g.connect(dest ?? sfxBus!);
  o.start(t0);
  o.stop(t0 + dur + 0.03);
}

function noiseBurst(dur: number, gain = 0.05, hp = 800) {
  const c = ac();
  if (!c) return;
  ensureBuses(c);
  const n = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, n, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = hp;
  const g = c.createGain();
  const t0 = c.currentTime;
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter);
  filter.connect(g);
  g.connect(sfxBus!);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

/** Chip note into music bus */
function chip(
  freq: number,
  dur: number,
  type: OscillatorType,
  gain: number,
  when: number,
) {
  if (!ctx || !musicBus || muted) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(Math.max(30, freq), when);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.exponentialRampToValueAtTime(gain, when + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  o.connect(g);
  g.connect(musicBus);
  o.start(when);
  o.stop(when + dur + 0.02);
}

// --- F-Zero / Big Blue–inspired racing patterns (original, midi). 0 = rest ---
// Fast ostinato bass + bright lead + syncopated arp. Not a copy of any track.
const MENU_LEAD = [
  76, 0, 79, 0, 83, 0, 79, 76, 74, 0, 76, 0, 79, 83, 86, 0, 84, 0, 83, 0, 79, 0,
  76, 74, 76, 0, 79, 0, 83, 79, 76, 0, 71, 0, 74, 0, 76, 0, 79, 76, 74, 0, 71, 0,
  67, 71, 74, 76, 79, 0, 76, 0, 74, 0, 71, 0, 67, 0, 71, 74, 76, 79, 76, 0,
];
const MENU_BASS = [
  40, 40, 40, 52, 40, 40, 47, 52, 38, 38, 38, 50, 38, 38, 45, 50, 36, 36, 36, 48,
  36, 36, 43, 48, 35, 35, 35, 47, 35, 43, 47, 50, 40, 40, 40, 52, 40, 40, 47, 52,
  38, 38, 38, 50, 38, 38, 45, 50, 43, 43, 43, 55, 43, 43, 50, 55, 40, 40, 47, 52,
  40, 47, 52, 55,
];

const PLAY_LEAD = [
  79, 83, 86, 83, 79, 76, 79, 0, 83, 86, 88, 86, 83, 79, 83, 0, 76, 79, 83, 86,
  88, 86, 83, 79, 76, 74, 76, 79, 83, 79, 76, 74, 71, 74, 76, 79, 83, 79, 76, 0,
  74, 76, 79, 83, 86, 83, 79, 0, 88, 86, 83, 79, 76, 79, 83, 86, 88, 91, 88, 86,
  83, 79, 76, 74,
];
const PLAY_BASS = [
  40, 40, 52, 40, 40, 40, 47, 52, 40, 40, 52, 40, 47, 52, 55, 52, 38, 38, 50, 38,
  38, 38, 45, 50, 38, 38, 50, 38, 45, 50, 52, 50, 36, 36, 48, 36, 36, 36, 43, 48,
  36, 36, 48, 36, 43, 48, 50, 48, 35, 35, 47, 35, 35, 43, 47, 50, 40, 40, 52, 40,
  47, 52, 55, 59,
];
const PLAY_ARP = [
  0, 91, 0, 95, 0, 91, 0, 88, 0, 91, 0, 95, 0, 98, 0, 95, 0, 88, 0, 91, 0, 88, 0,
  83, 0, 86, 0, 88, 0, 91, 0, 88, 0, 86, 0, 88, 0, 91, 0, 95, 0, 91, 0, 88, 0, 86,
  0, 83, 0, 95, 0, 98, 0, 95, 0, 91, 0, 100, 0, 98, 0, 95, 0, 91,
];
const PLAY_COUNTER = [
  0, 0, 67, 0, 0, 0, 71, 0, 0, 0, 67, 0, 0, 71, 74, 0, 0, 0, 66, 0, 0, 0, 69, 0,
  0, 0, 66, 0, 0, 69, 71, 0, 0, 0, 64, 0, 0, 0, 67, 0, 0, 0, 64, 0, 0, 67, 69, 0,
  0, 0, 62, 0, 0, 0, 66, 0, 0, 0, 67, 0, 0, 71, 74, 0,
];

function stopMusicScheduler() {
  if (musicTimer != null) {
    window.clearTimeout(musicTimer);
    musicTimer = null;
  }
}

function stopMusic() {
  stopMusicScheduler();
  musicMode = "off";
  musicStep = 0;
  if (musicBus && ctx) {
    const t = ctx.currentTime;
    musicBus.gain.cancelScheduledValues(t);
    musicBus.gain.setTargetAtTime(0.0001, t, 0.05);
  }
}

function scheduleStep(mode: "menu" | "play") {
  const c = ac();
  if (!c || !musicBus) return;
  ensureBuses(c);

  const drive = mode === "play" ? musicDrive : 0;
  // F-Zero energy: menu ~128, race ~168–190 with speed drive
  const bpm = (mode === "menu" ? 128 : 168) + drive * 28;
  const stepDur = 60 / bpm / 4; // 16th
  const when = c.currentTime + 0.02;
  const len = mode === "menu" ? MENU_LEAD.length : PLAY_LEAD.length;
  const i = musicStep % len;

  const lead = mode === "menu" ? MENU_LEAD[i]! : PLAY_LEAD[i]!;
  const bass = mode === "menu" ? MENU_BASS[i]! : PLAY_BASS[i]!;
  const arp = mode === "play" ? PLAY_ARP[i]! : 0;
  const counter = mode === "play" ? PLAY_COUNTER[i]! : 0;

  if (bass) {
    // Punchy racing bass — short saw + triangle body
    chip(midi(bass), stepDur * 1.35, "sawtooth", 0.07, when);
    chip(midi(bass), stepDur * 1.6, "triangle", 0.09, when);
  }
  if (lead) {
    chip(midi(lead), stepDur * 1.05, "square", 0.055, when);
    // slight octave sparkle like SNES brass
    if (i % 4 === 0) {
      chip(midi(lead + 12), stepDur * 0.55, "square", 0.02, when);
    }
  }
  if (arp) {
    chip(midi(arp), stepDur * 0.55, "square", 0.03, when);
  }
  if (counter) {
    chip(midi(counter), stepDur * 1.1, "triangle", 0.04, when);
  }

  // Hats: busy 16ths on race, lighter on menu
  if ((mode === "play" || i % 2 === 0) && ctx && musicBus) {
    const n = Math.floor(ctx.sampleRate * (mode === "play" ? 0.018 : 0.026));
    const buf = ctx.createBuffer(1, n, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let k = 0; k < n; k++) data[k] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.value = mode === "play" ? 4200 : 2800;
    const g = ctx.createGain();
    const hatGain =
      mode === "play" ? (i % 2 === 0 ? 0.032 : 0.016) : 0.014;
    g.gain.setValueAtTime(hatGain, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.025);
    src.connect(filter);
    filter.connect(g);
    g.connect(musicBus);
    src.start(when);
    src.stop(when + 0.03);
  }

  // Kick every beat + snare-ish on 2/4
  if (i % 4 === 0) {
    tone(98, 0.07, "sine", 0.055, 38, musicBus);
  }
  if (mode === "play" && i % 8 === 4) {
    noiseBurst(0.05, 0.035, 1200);
  }

  musicStep += 1;
  const baseGain = mode === "menu" ? 0.18 : 0.26 + drive * 0.07;
  musicBus.gain.setTargetAtTime(baseGain, c.currentTime, 0.08);

  stopMusicScheduler();
  musicTimer = window.setTimeout(
    () => scheduleStep(mode),
    Math.max(28, stepDur * 1000),
  );
}

function startMusic(mode: "menu" | "play") {
  if (muted) {
    musicMode = mode;
    return;
  }
  const c = ac();
  if (!c) return;
  ensureBuses(c);
  if (musicMode === mode && musicTimer != null) return;
  stopMusicScheduler();
  musicMode = mode;
  musicStep = 0;
  musicStartedAt = performance.now();
  if (musicBus) {
    musicBus.gain.cancelScheduledValues(c.currentTime);
    musicBus.gain.setValueAtTime(0.0001, c.currentTime);
  }
  scheduleStep(mode);
}

export const music = {
  unlock: () => {
    void ac();
  },
  menu: () => {
    if (musicMode === "menu" && musicTimer != null) {
      if (musicBus && ctx) {
        musicBus.gain.setTargetAtTime(0.18, ctx.currentTime, 0.08);
      }
      return;
    }
    startMusic("menu");
  },
  play: () => {
    if (musicMode === "play" && musicTimer != null) {
      if (musicBus && ctx) {
        musicBus.gain.setTargetAtTime(
          0.26 + musicDrive * 0.07,
          ctx.currentTime,
          0.08,
        );
      }
      return;
    }
    startMusic("play");
  },
  stop: () => stopMusic(),
  /** Soften during crash/continue overlay */
  duck: () => {
    if (!musicBus || !ctx || musicMode === "off") return;
    musicBus.gain.setTargetAtTime(0.06, ctx.currentTime, 0.1);
  },
  /** 0..1 from speed gear — tightens tempo + mix */
  setDrive: (t: number) => {
    musicDrive = Math.max(0, Math.min(1, t));
  },
  mode: () => musicMode,
  ageMs: () => (musicMode === "off" ? 0 : performance.now() - musicStartedAt),
};

export const sfx = {
  unlock: () => {
    void ac();
    music.unlock();
  },

  /** Lane change — dir -1 left / +1 right. */
  lane: (dir: -1 | 1 = 1) => {
    if (dir < 0) {
      tone(620, 0.055, "square", 0.045, 380);
      tone(310, 0.04, "triangle", 0.025, 220);
    } else {
      tone(380, 0.055, "square", 0.045, 640);
      tone(220, 0.04, "triangle", 0.025, 340);
    }
  },

  /** @deprecated use lane() */
  swipe: () => sfx.lane(1),

  jump: () => {
    tone(330, 0.05, "square", 0.04, 660);
    setTimeout(() => tone(660, 0.08, "triangle", 0.05, 990), 40);
  },

  jumpPickup: () => {
    tone(523, 0.05, "square", 0.04, 784);
    setTimeout(() => tone(784, 0.07, "triangle", 0.045, 1047), 40);
  },

  /** Active vehicle power activation. */
  power: () => {
    tone(440, 0.05, "square", 0.04, 880);
    setTimeout(() => tone(880, 0.08, "triangle", 0.05, 1320), 45);
    setTimeout(() => tone(660, 0.1, "sawtooth", 0.03, 220), 90);
  },

  /** Bulldog crate smash. */
  crush: () => {
    noiseBurst(0.08, 0.06, 280);
    tone(180, 0.1, "square", 0.05, 90);
  },

  coin: () => {
    tone(784, 0.06, "square", 0.05, 1175);
    setTimeout(() => tone(1568, 0.09, "triangle", 0.045), 45);
  },

  crash: () => {
    noiseBurst(0.22, 0.08, 200);
    tone(160, 0.32, "sawtooth", 0.1, 45);
    tone(90, 0.4, "sine", 0.07, 35);
  },

  start: () => {
    tone(392, 0.08, "square", 0.05);
    setTimeout(() => tone(523, 0.08, "square", 0.05), 70);
    setTimeout(() => tone(659, 0.12, "square", 0.055), 140);
  },

  continue: () => {
    tone(523, 0.08, "triangle", 0.05, 784);
    setTimeout(() => tone(784, 0.12, "triangle", 0.055, 1047), 70);
  },

  speedUp: () => {
    tone(440, 0.05, "square", 0.04, 660);
    setTimeout(() => tone(660, 0.05, "square", 0.045, 880), 50);
    setTimeout(() => tone(880, 0.08, "square", 0.05, 1175), 100);
  },

  ui: () => tone(520, 0.04, "triangle", 0.03),

  /** Soft deny / can't afford. */
  deny: () => {
    tone(220, 0.06, "square", 0.035, 140);
    setTimeout(() => tone(150, 0.09, "triangle", 0.03, 90), 55);
  },

  gameOver: () => {
    tone(392, 0.12, "square", 0.05, 330);
    setTimeout(() => tone(330, 0.14, "square", 0.05, 262), 110);
    setTimeout(() => tone(220, 0.28, "triangle", 0.055, 110), 230);
  },
};
